import { useContext, useState } from 'react';
import { useSelector } from 'react-redux';
import * as R from 'ramda';

import DeleteModal from '@atom/components/common/DeleteModal';
import SchemaDetailContext from '@atom/components/schemaDetail/SchemaDetailContext';
import {
  RadioGroup,
  Select,
  SelectChangeEvent,
  Snackbar,
  TextField,
} from '@atom/mui';
import { budgetUnitsSelector } from '@atom/selectors/preferencesSelectors';
import { SchemaBudget, SchemaBudgetType } from '@atom/types/schema';

import classes from './costDetail.module.css';

const { MenuItem } = Select;
const { RadioButton } = RadioGroup;

const styles = {
  unitOption: {
    textTransform: 'uppercase',
  },
  inputStyles: {
    width: '200px',
  },
};

const CONFIRM_CONTENT = {
  type: {
    title: 'Change Cost and Unit?',
    content:
      'Changing the cost and unit will remove all conversion units. Are you sure you want to continue?',
  },
  unit: {
    title: 'Change Base Unit?',
    content:
      'Changing the base unit will remove all conversion units. Are you sure you want to continue?',
  },
};

interface Props {
  budget: SchemaBudget;
  setBudget: (budget: SchemaBudget) => void;
  updateBudget: (budget: SchemaBudget) => void;
  /**
   * changing the cost type or unit asks to remove conversion units first
   */
  hasConversionUnits: boolean;
  clearConversionUnits: () => Promise<void>;
}

const CostSection = ({
  budget,
  setBudget,
  updateBudget,
  hasConversionUnits,
  clearConversionUnits,
}: Props) => {
  const { schemaTree, refetchSchemaTree } = useContext(SchemaDetailContext);
  const budgetUnits = useSelector(budgetUnitsSelector);

  // the cost type to switch to, or 'unit' to open the unit dropdown, once confirmed
  const [confirmTarget, setConfirmTarget] = useState<
    SchemaBudgetType | 'unit' | null
  >(null);
  const [clearing, setClearing] = useState<boolean>(false);
  const [unitSelectOpen, setUnitSelectOpen] = useState<boolean>(false);

  const { rate, type, unit } = budget;

  const updateRate = event => {
    const newTimeRate = event.target.value
      ? Math.abs(Number(event.target.value))
      : 0;

    const updatedBudget = {
      ...budget,
      rate: newTimeRate,
    };

    setBudget(updatedBudget);

    if (!R.isNil(newTimeRate)) {
      updateBudget(updatedBudget);
    }
  };

  const updateUnit = (event: SelectChangeEvent) => {
    const updatedBudget = {
      ...budget,
      unit: event.target.value,
    };

    setBudget(updatedBudget);
    updateBudget(updatedBudget);
  };

  const applyBudgetType = (budgetType: SchemaBudgetType) => {
    const updatedBudget = {
      ...budget,
      rate: 0,
      unit: 'sq ft',
      type: budgetType,
    };

    setBudget(updatedBudget);
    updateBudget(updatedBudget);
  };

  const updateBudgetType = event => {
    const budgetType = event.target.value as SchemaBudgetType;

    if (hasConversionUnits) {
      setConfirmTarget(budgetType);
    } else {
      applyBudgetType(budgetType);
    }
  };

  const openUnitSelect = () => {
    if (hasConversionUnits) {
      setConfirmTarget('unit');
    } else {
      setUnitSelectOpen(true);
    }
  };

  const confirmClearConversionUnits = async () => {
    setClearing(true);

    try {
      await clearConversionUnits();

      if (confirmTarget === 'unit') {
        refetchSchemaTree();
        setUnitSelectOpen(true);
      } else {
        applyBudgetType(confirmTarget);
      }
    } catch (error) {
      Snackbar.error({ message: 'Failed to remove conversion units.' });
      refetchSchemaTree();
    }

    setClearing(false);
    setConfirmTarget(null);
  };

  const isDisabled = schemaTree?.isPublished;

  const confirmContent =
    CONFIRM_CONTENT[confirmTarget === 'unit' ? 'unit' : 'type'];

  const unitSelectProps = {
    open: unitSelectOpen,
    onOpen: openUnitSelect,
    onClose: () => setUnitSelectOpen(false),
  };

  return (
    <>
      <div className={classes.costContainer}>
        <div className={classes.costUnitTitle}>Cost & Unit</div>
        <RadioGroup value={type} onChange={updateBudgetType}>
          <RadioButton
            value={SchemaBudgetType.VARIABLE}
            label="Variable"
            disabled={isDisabled}
            data-cy="costSectionVariableRadio"
          />
          <div className={classes.optionSubtext}>
            Set the cost and unit at the individual inventory item level.
          </div>
          <RadioButton
            value={SchemaBudgetType.VARIABLE_COST}
            label="Variable Cost"
            disabled={isDisabled}
            data-cy="costSectionVariableCostRadio"
          />
          <div className={classes.optionSubtext}>
            Set a fixed unit for all inventory of this type.
          </div>
          {type === SchemaBudgetType.VARIABLE_COST && (
            <div className={classes.costInputContainer}>
              <Select
                label="Unit"
                fullWidth
                InputProps={{
                  style: styles.unitOption,
                }}
                style={styles.inputStyles}
                onChange={updateUnit}
                value={unit}
                disabled={isDisabled}
                {...unitSelectProps}
                data-cy="costSectionUnitSelect"
              >
                {budgetUnits.map(option => {
                  return (
                    <MenuItem
                      key={option}
                      value={option || null}
                      style={styles.unitOption}
                    >
                      {option}
                    </MenuItem>
                  );
                })}
              </Select>
            </div>
          )}
          <RadioButton
            value={SchemaBudgetType.FIXED}
            label="Fixed"
            disabled={isDisabled}
            data-cy="costSectionFixedRadio"
          />
          <div className={classes.optionSubtext}>
            Set a fixed cost and unit for all inventory of this type.
          </div>
          {type === SchemaBudgetType.FIXED && (
            <div className={classes.costInputContainer}>
              <TextField
                label="Cost"
                value={rate}
                type="number"
                style={styles.inputStyles}
                onChange={updateRate}
                slotProps={{
                  input: {
                    startAdornment: '$',
                  },
                }}
                disabled={isDisabled}
              />
              <div className={classes.separator}>per</div>
              <Select
                label="Unit"
                fullWidth
                InputProps={{
                  style: styles.unitOption,
                }}
                style={styles.inputStyles}
                onChange={updateUnit}
                value={unit}
                disabled={isDisabled}
                {...unitSelectProps}
                data-cy="costSectionUnitSelect"
              >
                {budgetUnits.map(option => {
                  return (
                    <MenuItem
                      key={option}
                      value={option || null}
                      style={styles.unitOption}
                    >
                      {option}
                    </MenuItem>
                  );
                })}
              </Select>
            </div>
          )}
        </RadioGroup>
      </div>
      <DeleteModal
        open={confirmTarget !== null}
        loading={clearing}
        title={confirmContent.title}
        content={confirmContent.content}
        confirmText="Remove"
        onCancel={() => setConfirmTarget(null)}
        onConfirm={confirmClearConversionUnits}
        dataCy="costSectionRemoveConversionUnitsModal"
      />
    </>
  );
};

export default CostSection;
